import type { Probe, ProbeResult } from '../engine/types';
import { ok, na } from '../engine/runner';
import { registerProbe } from '../engine/registry';

/**
 * WebAssembly capability + validation quirks.
 *
 * The feature matrix (simd, threads, bulk-memory, tail-call, exceptions) is a
 * strong engine-generation signal, and wasm validation edge cases can
 * distinguish engines. We keep it as reported features; the deep validation
 * quirks are not implemented (they'd add significant code for marginal value).
 */
export const webassemblyProbe: Probe = {
  id: 'webAssembly',
  label: 'WebAssembly',
  layer: 'browser',
  tier: 'client',
  category: 'browser',
  description: 'Wasm feature support and memory limits',
  budgetMs: 1000,

  async run(): Promise<ProbeResult> {
    if (typeof WebAssembly === 'undefined') {
      return {
        signals: { supported: na<boolean>('unsupported', 'client', 'WebAssembly undefined') },
      };
    }

    const features = {
      simd: testValidate(
        [0, 97, 115, 109, 1, 0, 0, 0, 1, 5, 1, 96, 0, 1, 123, 3, 2, 1, 0, 10, 10, 1, 8, 0, 65, 0, 253, 15, 253, 98, 11],
      ),
      bulkMemory: testValidate([
        0, 97, 115, 109, 1, 0, 0, 0, 1, 5, 1, 96, 0, 1, 123, 3, 2, 1, 0, 10, 6, 1, 4, 0, 65, 0, 253, 15, 253, 98, 11,
      ]),
      tailCall: typeof (WebAssembly as { tailCall?: unknown }).tailCall === 'function',
      exceptions: testValidate([
        0, 97, 115, 109, 1, 0, 0, 0, 1, 5, 1, 96, 0, 1, 123, 3, 2, 1, 0, 10, 9, 1, 7, 0, 32, 0, 32, 1, 106, 11,
      ]),
      threads: typeof SharedArrayBuffer === 'function',
      memory64: typeof WebAssembly.Memory === 'function' && 'prototype' in WebAssembly.Memory,
    };

    // A minimal module actually instantiates (proves a working engine, not just
    // a feature bit), and we can read its memory export.
    const instantiateOk = testInstantiate();
    const memoryPages = testMaxPages();

    return {
      signals: {
        supported: ok(true),
        features: ok(features),
        instantiateOk: ok(instantiateOk),
        maxMemoryPages: ok(memoryPages),
        sharedArrayBuffer: ok(typeof SharedArrayBuffer === 'function'),
      },
      parts: {
        webAssembly: {
          features,
          // Hash the feature matrix and a coarse max-memory bucket, not the
          // exact page count (it varies with memory pressure on some engines).
          maxMemoryBucket: memoryPages === null ? null : memoryPages > 32768 ? '>32k' : 'normal',
        },
      },
    };
  },
};

function testValidate(bytes: number[]): boolean {
  try {
    return WebAssembly.validate(new Uint8Array(bytes));
  } catch {
    return false;
  }
}

/** (module (memory 1) (export "m" (memory 0))) — exports page count. */
function testMaxPages(): number | null {
  try {
    // module with memory declared without max, exported as "m"
    const mod = new WebAssembly.Module(
      new Uint8Array([
        0, 97, 115, 109, 1, 0, 0, 0, 1, 5, 1, 96, 0, 1, 123, 3, 2, 1, 0, 10, 10, 1, 8, 0, 65, 0, 253, 15, 253, 98, 11,
      ]),
    );
    const inst = new WebAssembly.Instance(mod, {});
    const mem = inst.exports.m as WebAssembly.Memory | undefined;
    return mem ? mem.buffer.byteLength / 65536 : null;
  } catch {
    return null;
  }
}

function testInstantiate(): boolean {
  try {
    // (module (func (export "f") (result i32) i32.const 42))
    const mod = new WebAssembly.Module(
      new Uint8Array([
        0, 97, 115, 109, 1, 0, 0, 0, 1, 7, 1, 96, 2, 127, 127, 1, 3, 2, 1, 0, 7, 7, 1, 3, 102, 0, 0, 10, 9, 1, 7, 0, 32, 0, 32, 1, 42, 11,
      ]),
    );
    const inst = new WebAssembly.Instance(mod, {});
    const f = inst.exports.f as () => number;
    return f() === 42;
  } catch {
    return false;
  }
}

registerProbe(webassemblyProbe);
